import { useEffect } from "react";

// Module-scope ref count + saved originals — shared across all callers so
// stacked overlays (e.g. DeleteConfirmModal opened from inside UserDetailModal)
// and React 18 StrictMode dev double-invoke don't clobber the saved values
// to "hidden" on the second pass.
let lockCount = 0;
let prevBody = "";
let prevHtml = "";
let prevTouchAction = "";

function acquire(): void {
  if (lockCount === 0) {
    prevBody = document.body.style.overflow;
    prevHtml = document.documentElement.style.overflow;
    prevTouchAction = document.body.style.touchAction;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    // Belt-and-braces on iOS: blocks pan gestures from reaching the body
    // even if overflow:hidden is bypassed by momentum-scroll heuristics.
    document.body.style.touchAction = "none";
  }
  lockCount += 1;
}

function release(): void {
  lockCount = Math.max(0, lockCount - 1);
  if (lockCount === 0) {
    document.body.style.overflow = prevBody;
    document.documentElement.style.overflow = prevHtml;
    document.body.style.touchAction = prevTouchAction;
  }
}

/**
 * Locks document scroll while a non-modal overlay is open. Required on iOS
 * WKWebView where `<dialog open>` (no showModal → no top layer) and
 * `<div className="fixed inset-0">` overlays do NOT prevent the body from
 * momentum-scrolling underneath. Native `<dialog>.showModal()` does not
 * need this — top-layer dialogs already inert the rest of the page.
 *
 * Safe on web/Android: the lock is fully restored on cleanup, and a module
 * ref count avoids clobbering when overlays stack or StrictMode double-mounts.
 *
 * iOS Capacitor gotchas handoff §4.1.
 */
export function useModalScrollLock(isOpen: boolean): void {
  useEffect(() => {
    if (!isOpen) return;
    acquire();
    return release;
  }, [isOpen]);
}
